import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { AlertController, ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';

import { TASK_DELIVERY_PORT, TaskDeliveryPort } from './task-delivery.port';
import {
  TASK_ACCEPTED_MIME_TYPES,
  TASK_FILE_ACCEPT,
  TaskConstraints,
  TaskData,
  TaskResource,
  TaskSavedEvent,
  TaskSubmitResponse,
} from './task-delivery.model';
import { ToastService } from 'src/app/service/toast-service/toast.service';

type TaskDeliveryPhase = 'loading' | 'error' | 'ready' | 'sending' | 'sent';

/** The accordion sections, in production's order. One open at a time. */
type TaskSection = 'indicaciones' | 'recursos' | 'documentos' | 'comentarios';

const BYTES_PER_MB = 1024 * 1024;

/** Production's own copy, kept word for word where it is the same screen. */
const SEND_ERROR = 'No pudimos enviar tu tarea. Inténtalo nuevamente.';
const LOAD_ERROR = 'No pudimos cargar la tarea. Inténtalo nuevamente.';

/** Estados de la tarea (los del backend) en los que el alumno puede adjuntar y enviar. */
const EDITABLE_STATUSES: readonly string[] = ['pending', 'observed'];

/**
 * Entrega de una tarea (`project`), extraída de la copia que `pages/tema` repite en cuatro páginas
 * (`tema.page.html:1082-1255` + `tema.page.ts:6022-6240`). Es la **primera vez** que vive en un
 * componente: las copias de producción no se tocan (decisión de la dueña), así que este componente
 * solo lo consume la ruta por ahora.
 *
 * Qué es de este componente y qué no:
 * - **Sí**: las cuatro secciones del acordeón, la validación de cantidad / tamaño / tipo, armar el
 *   `FormData` con el mismo shape que producción, la descarga de recursos (con la misma confirmación
 *   de Wi-Fi), y las fases de carga → listo → enviando → enviado.
 * - **No**: de dónde sale ni adónde va el dato. Eso es el {@link TaskDeliveryPort}, inyectado con
 *   `TASK_DELIVERY_PORT`. Por defecto habla con `ApiService`; la ruta provee el suyo, que pasa por
 *   `LearningPathService`. Así el componente nunca decide por su cuenta una llamada de la ruta.
 *
 * Diferencias a propósito con producción:
 * - Un error al enviar se muestra **dentro** del componente y conserva los archivos. Producción cierra
 *   el modal en cualquier caso, y en la ruta no hay modal que cerrar.
 * - Tras enviar se vuelve a pedir la tarea, para que los archivos aparezcan como ya guardados.
 *
 * `variant` solo cambia el relleno: `inline` (el de la ruta, sin chrome alrededor) lo trae; `modal`
 * queda sin relleno porque el marco del modal ya lo pone. Es para una futura migración de producción.
 */
@Component({
  selector: 'app-task-delivery',
  templateUrl: './task-delivery.component.html',
  styleUrls: ['./task-delivery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskDeliveryComponent implements OnDestroy {
  /** Optional subtitle, as production's modal shows the course name under its title. */
  @Input() courseName = '';
  @Input() variant: 'modal' | 'inline' = 'inline';

  /** Fires once the task is sent. The data is already re-requested by the time it does. */
  @Output() saved = new EventEmitter<TaskSavedEvent>();

  @HostBinding('class.task-delivery--inline')
  get isInline(): boolean {
    return this.variant === 'inline';
  }

  @Input()
  set projectId(value: number) {
    if (value === this._projectId) return;
    this._projectId = value;
    this.load();
  }
  get projectId(): number {
    return this._projectId;
  }
  private _projectId!: number;

  /**
   * Estado de la tarea según el backend (`pending`, `in_review`, `observed`, `disapproved`, `passed`).
   * Solo `pending` y `observed` dejan adjuntar y enviar. `null` = quien monta el componente no lo
   * sabe; entonces se infiere de los archivos guardados.
   */
  @Input()
  set status(value: string | null) {
    if (value === this._status) return;
    this._status = value;
    // Si el nuevo estado vuelve a permitir el envío (p. ej. `observed`), el "ya enviado" de esta
    // sesión deja de mandar. Con `in_review` se queda, junto con el mensaje del backend.
    if (this.phase === 'sent' && (value === null || EDITABLE_STATUSES.includes(value))) {
      this.phase = 'ready';
      this.successMsg = '';
    }
  }
  get status(): string | null {
    return this._status;
  }
  private _status: string | null = null;

  phase: TaskDeliveryPhase = 'loading';
  task: TaskData | null = null;
  /** The learner's files, reference-free: production keeps this list inside `taskData` itself. */
  userResources: TaskResource[] = [];
  openSection: TaskSection | null = 'indicaciones';
  /** Estado de la tarea según el último envío. Vacío hasta que el backend lo informe. */
  statusLabel: string | null = null;
  /** Error de envío o de carga, mostrado en línea. */
  errorMsg = '';
  /** Mensaje del backend tras un envío exitoso. */
  successMsg = '';

  private loadSub?: Subscription;
  private submitSub?: Subscription;

  constructor(
    @Inject(TASK_DELIVERY_PORT) private port: TaskDeliveryPort,
    private toastCtrl: ToastController,
    private alertCtrl: AlertController,
    private changeDetector: ChangeDetectorRef,
    private toastService: ToastService,
  ) {}

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
    this.submitSub?.unsubscribe();
  }

  // ── Carga ───────────────────────────────────────────────────────────────

  load(): void {
    this.loadSub?.unsubscribe();
    this.phase = 'loading';
    this.errorMsg = '';
    this.successMsg = '';

    this.loadSub = this.port.getTask(this._projectId).subscribe({
      next: (task) => {
        this.applyTask(task);
        this.phase = 'ready';
        this.changeDetector.markForCheck();
      },
      error: () => {
        this.task = null;
        this.phase = 'error';
        this.errorMsg = LOAD_ERROR;
        this.changeDetector.markForCheck();
      },
    });
  }

  /**
   * Re-reads the task after a send without touching the phase, so the success message stays on
   * screen while the learner's files switch from "to send" to "stored". A failure here is silent:
   * the send already succeeded, and the list simply keeps what was on screen.
   */
  private refreshTask(): void {
    this.loadSub?.unsubscribe();
    this.loadSub = this.port.getTask(this._projectId).subscribe({
      next: (task) => {
        this.applyTask(task);
        this.changeDetector.markForCheck();
      },
    });
  }

  private applyTask(task: TaskData): void {
    this.task = task;
    this.userResources = [...(task.user_resources ?? [])];
  }

  // ── Presentación ────────────────────────────────────────────────────────

  get constraints(): TaskConstraints | null {
    return this.task?.constraints ?? null;
  }

  /** The upload control exists only when the task says how many and how big. */
  get canUpload(): boolean {
    return this.constraints !== null;
  }

  /**
   * The task is closed to the learner: neither attaching nor sending. The backend's `status` decides
   * (only `pending` / `observed` are open); `phase === 'sent'` bridges the gap between a send and the
   * refresh that brings the new status. Without a status, stored files stand in for it.
   */
  get isDelivered(): boolean {
    if (this.phase === 'sent') return true;
    if (this._status !== null) return !EDITABLE_STATUSES.includes(this._status);
    return this.userResources.some((resource) => this.isStored(resource));
  }

  /** Files picked and not yet sent: the only thing "Enviar" has to send. */
  get hasPendingFiles(): boolean {
    return this.userResources.some((resource) => resource.type_resource === 'file' && !!resource.upload);
  }

  get canAddFile(): boolean {
    const max = this.constraints?.max_quantity_upload_files ?? 0;
    return !this.isDelivered && this.userResources.length < max;
  }

  get canSend(): boolean {
    return this.phase === 'ready' && !this.isDelivered && this.hasPendingFiles;
  }

  get isSending(): boolean {
    return this.phase === 'sending';
  }

  readonly acceptTypes = TASK_FILE_ACCEPT;

  toggleSection(section: TaskSection): void {
    this.openSection = this.openSection === section ? null : section;
  }

  isOpen(section: TaskSection): boolean {
    return this.openSection === section;
  }

  sizeMb(resource: TaskResource): string {
    return ((resource.size ?? 0) / BYTES_PER_MB).toFixed(2);
  }

  resourceName(resource: TaskResource): string {
    return resource.name || resource.title || '';
  }

  isStored(resource: TaskResource): boolean {
    return resource.type_resource === 'media';
  }

  // ── Archivos ────────────────────────────────────────────────────────────

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.constraints || !this.canAddFile) return;

    this.openSection = 'documentos';

    if (file.size / BYTES_PER_MB > this.constraints.max_size_upload_files) {
      this.toastService.presentToast("error", `El limite máximo por archivo es de ${this.constraints.max_size_upload_files} MB.`, 3000, "top", false);
      return;
    }

    if (!TASK_ACCEPTED_MIME_TYPES.has(file.type)) {
      this.toastService.presentToast("error", `Recuerda que solo puede subir archivos de tipo: imagen, video, pdf y office.`, 3000, "top", false);
      return;
    }

    this.userResources = [
      ...this.userResources,
      { name: file.name, size: file.size, type_resource: 'file', upload: file },
    ];
    this.changeDetector.markForCheck();
  }

  removeResource(index: number): void {
    if (this.isSending || this.isDelivered) return;
    this.userResources = this.userResources.filter((_, i) => i !== index);
    this.changeDetector.markForCheck();
  }

  // ── Descarga ────────────────────────────────────────────────────────────

  /** Same confirmation production shows before any download. */
  async confirmDownload(resource: TaskResource): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: 'Descargar',
      message: 'Te sugerimos utilizar una red WI-FI',
      mode: 'ios',
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        { text: 'Continuar', handler: () => this.port.downloadResource(resource) },
      ],
    });
    await alert.present();
  }

  // ── Envío ───────────────────────────────────────────────────────────────

  send(): void {
    if (!this.canSend) return;

    this.phase = 'sending';
    this.errorMsg = '';
    this.successMsg = '';
    this.submitSub?.unsubscribe();

    this.submitSub = this.port.submitTask(this._projectId, this.buildFormData()).subscribe({
      next: (response) => this.onSubmitted(response),
      error: () => this.onSubmitFailed(),
    });
  }

  /**
   * Same shape production sends: picked files under `files[]`, stored media under
   * `multimedias[i][key]` with every key the resource carries.
   */
  private buildFormData(): FormData {
    const formData = new FormData();

    this.userResources
      .filter((resource) => resource.type_resource === 'file' && resource.upload)
      .forEach((resource) => formData.append('files[]', resource.upload as File));

    this.userResources
      .filter((resource) => resource.type_resource === 'media')
      .forEach((media, index) => {
        Object.keys(media).forEach((key) => {
          formData.append(`multimedias[${index}][${key}]`, String(media[key]));
        });
      });

    return formData;
  }

  private onSubmitted(response: TaskSubmitResponse): void {
    if (response?.type !== 'success') {
      this.onSubmitFailed();
      return;
    }

    const data = response.data ?? {};
    this.successMsg = data.msg ?? '';
    this.statusLabel = data.status_label ?? this.statusLabel;
    this.phase = 'sent';
    this.saved.emit({
      msg: this.successMsg,
      status_label: data.status_label ?? null,
      status_code: data.status_code ?? null,
    });

    // Lo que quedó guardado reemplaza a lo que se acaba de elegir.
    this.refreshTask();
  }

  private onSubmitFailed(): void {
    this.phase = 'ready';
    this.errorMsg = SEND_ERROR;
    this.changeDetector.markForCheck();
  }

}
